import { Component, OnInit } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, ColGroupDef, Column, ColumnApi, FilterModifiedEvent, GridApi, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import { ExportTrackingService } from 'src/app/_services/export-tracking.service';
import { AlertService } from 'src/app/_services/alert.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { FxService } from './fx-service';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Component({
  selector: 'cross-border-transaction-report',
  templateUrl: './cross-border-transaction-report.component.html',
  styleUrls: ['./cross-border-transaction-report.component.scss']
})
export class CrossBorderTransactionReportComponent implements OnInit {

  // Date pickers
  fromPickerOpen = false;
  toPickerOpen = false;

  searchFromDate!: NgbDateStruct;
  searchToDate!: NgbDateStruct;

  maxDateFrom!: NgbDateStruct;
  minDate!: NgbDateStruct;
  maxDate!: NgbDateStruct;

  // Dropdown data
  merchantStatusdata: any[] = [];
  merchantListdata: any[] = [];
  CurrencyList: any[] = [];
  currencyFromList: any[] = [];
  currencyToList: any[] = [];

  // Dropdown selected values (FIXED: separate models)
  merchantList: string | null = null;
  status: string | null = null;
  currencyFrom: string | null = null;
  currencyTo: string | null = null;

  // Grid + pagination
  pageSize = 10;
  pageNumber = 0; // backend page index (0-based)
  currentPage = 1; // UI page (1-based)
  collectionSize = 0;

  rowData: any[] = [];
  private baseData: any[] = [];         // original rows (without detail rows)
  expandedTxnId: string | null = null;  // only one expanded at a time (optional)

  pinnedBottomRowData: any[] = [];

  private gridApi!: GridApi;
  private gridColumnApi!: ColumnApi;
  private filterObj: any = {};

  columnDefs: ColDef[] = [
    // {
    //   field: 'isCrossTxn',
    //   headerName: 'Is Cross Txn',
    //   minWidth: 140,
    //   cellRenderer: (p: any) => (p.value ? 'True' : 'False')
    // },
    {
      field: 'isCrossTxn',
      headerName: 'Is Cross Txn',
      minWidth: 160,
      cellRenderer: (params: any) => {
        // FALSE → just centered text
        if (!params.data?.isCrossTxn) {
          return `
            <div style="
              display:flex;
              align-items:center;
              justify-content:center;
              height:100%;
              font-weight:600;
            ">
              False
            </div>
          `;
        }

        // TRUE → arrow + text
        const expanded = params.data.transactionId === this.expandedTxnId;
        const arrow = expanded ? '▼' : '▶';
        const arrowColor = expanded ? '#0d6efd' : '#888';


        return `
          <div style="
            display:flex;
            align-items:center;
            justify-content:center;
            gap:6px;
            height:100%;
            cursor:pointer;
            font-weight:600;
          ">
            <span style="color:${arrowColor}; font-size:12px;">${arrow}</span>
            <span>True</span>
          </div>
        `;
      }
    },
    { field: 'transactionId', headerName: 'Transaction ID', minWidth: 260 },
    { field: 'merchantCode', headerName: 'Merchant', minWidth: 180 },
    { field: 'displayName', headerName: 'Display Name', minWidth: 180 },
    { field: 'currencyPair', headerName: 'Currency Pair', minWidth: 120 },
    { field: 'amount', headerName: 'Amount', minWidth: 120 },
    { field: 'ask', headerName: 'Ask', minWidth: 120 },
    { field: 'bid', headerName: 'Bid', minWidth: 120 },
    { field: 'mid', headerName: 'Mid', minWidth: 120 },
    { field: 'exchangeRate', headerName: 'Rate', minWidth: 120 },
    { field: 'convertedAmount', headerName: 'Converted', minWidth: 130 },
    { field: 'status', headerName: 'Status', minWidth: 120 },
    { field: 'timestamp', headerName: 'Timestamp', minWidth: 200 },
  ];

  columnDefsCopy: ColDef[] = [...this.columnDefs];

  tableSelectedColumn: (ColDef | ColGroupDef)[] = [...this.columnDefs];

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 150,
    filter: false,
    resizable: true,
    headerClass: 'text-center',
    cellClass: 'text-center'
  };

  constructor(
    private fxService: FxService,
    private alertService: AlertService,
    private exportTrackingService: ExportTrackingService
  ) { }

  ngOnInit(): void {
    this.initDefaultDates();
    this.initDropdowns();
    this.initCurrencyList();
  }

  private initDefaultDates(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    this.searchFromDate = {
      year: yesterday.getFullYear(),
      month: yesterday.getMonth() + 1,
      day: yesterday.getDate()
    };

    this.searchToDate = { ...this.searchFromDate };

    this.maxDate = { ...this.searchToDate };     // block today & future
    this.maxDateFrom = { ...this.maxDate };
    this.minDate = { ...this.searchFromDate };
  }

  private initDropdowns(): void {
    this.merchantStatusdata = [
      { FieldValue: 'SUCCESS', FieldText: 'Success' },
      { FieldValue: 'FAILED', FieldText: 'Failed' }
    ];

    // optional: default until API loads
    this.merchantListdata = [];

    this.fxService.getFxMerchantList().subscribe({
      next: (res: string[]) => {
        if (Array.isArray(res)) {
          this.merchantListdata = [ // optional
            ...res.map((m: string) => ({
              FieldValue: m,
              FieldText: m
            }))
          ];
        } else {
          this.alertService.errorAlert({ text: 'Unexpected response for merchant list' });
        }
      },
      error: (err) => {
        console.error('Failed to fetch merchant list', err);
        this.alertService.errorAlert({ text: 'Failed to load merchant list. Please try again later.' });
      }
    });
  }


  private initCurrencyList(): void {
    this.fxService.getFxCurrencyList().subscribe({
      next: (res: any[]) => {
        if (Array.isArray(res)) {
          this.CurrencyList = res.map(item => ({
            FieldValue: item.currencyCode, // used as value
            FieldText: `${item.currencyCode} - ${item.currencyName}` // shown in dropdown
          }));

          // keep both lists in sync
          this.currencyFromList = [...this.CurrencyList];
          this.currencyToList = [...this.CurrencyList];
        } else {
          this.alertService.errorAlert({
            text: 'Unexpected response for currency list'
          });
        }
      },
      error: (err) => {
        console.error('Failed to fetch currency list', err);
        this.alertService.errorAlert({
          text: 'Failed to load currency list. Please try again later.'
        });
      }
    });
  }



  // ===== Display helpers =====
  get fromDisplay(): string { return this.buildDisplay(this.searchFromDate); }
  get toDisplay(): string { return this.buildDisplay(this.searchToDate); }

  private buildDisplay(date: NgbDateStruct): string {
    if (!date) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
  }

  // ===== Date pickers =====
  toggleFromPicker() { this.fromPickerOpen = !this.fromPickerOpen; }
  closeFromPicker() { this.fromPickerOpen = false; }

  toggleToPicker() { this.toPickerOpen = !this.toPickerOpen; }
  closeToPicker() { this.toPickerOpen = false; }

  applyFrom() {
    this.minDate = this.searchFromDate;
    this.fromPickerOpen = false;
  }

  applyTo() {
    this.maxDateFrom = this.searchToDate;
    this.toPickerOpen = false;
  }

  // ===== Search =====
  onSearchFilter() {
    // used by "Search" button
    this.pageNumber = 0;
    this.currentPage = 1;
    this.onSearchPayments();
  }

  onPageSizeChanges($event: Event) {
    this.gridApi.paginationSetPageSize(this.pageSize)
    this.gridApi.applyTransaction({ remove: this.rowData })
    this.gridApi.showLoadingOverlay();
    this.pageNumber = 0;
    this.onSearchPayments();
  }

  onSearchPayments(): void {
    const fromDate = this.fromDisplay;
    const toDate = this.toDisplay;

    const params: any = {
      fromDate,
      toDate,
      size: this.pageSize,
      page: this.pageNumber
    };

    if (this.merchantList && this.merchantList !== 'ALL') {
      params.merchantCode = this.merchantList;
    }

    if (this.status && this.status !== 'ALL') {
      params.status = this.status;
    }

    if (this.currencyFrom && this.currencyFrom !== 'ALL') {
      params.fromCurrency = this.currencyFrom;
    }

    if (this.currencyTo && this.currencyTo !== 'ALL') {
      params.toCurrency = this.currencyTo;
    }

    if(this.status != null && this.status !== 'ALL'){
      params.status = this.status;
    }

    this.fxService.getFxTransactionList(params).subscribe((response: any) => {
      const txns = response?.transactions ?? [];
      this.baseData = txns.map((t: any) => ({
        transactionId: t.transactionId,
        merchantCode: t.merchantCode,
        displayName: t.displayName,
        currencyPair: `${t.fromCurrency} / ${t.toCurrency}`,
        amount: t.amount,
        ask: t.ask,
        bid: t.bid,
        mid: t.midPrice,
        exchangeRate: t.exchangeRate,
        convertedAmount: t.convertedAmount,
        status: t.status,
        timestamp: t.timestamp,
        isCrossTxn: !!t.isCrossTxn,

        // ✅ details for expanded row (only used when isCrossTxn=true)
        details: null,
        detailsLoaded: false,   // since already in same response
        detailsLoading: false
      }));

      this.expandedTxnId = null;
      this.rowData = [...this.baseData];
      this.collectionSize = response?.totalElements ?? this.baseData.length;

      this.gridApi?.setRowData(this.rowData);
      this.gridApi?.hideOverlay();
      setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    }, error => {
      console.error('Failed to fetch transactions', error);
      this.alertService.errorAlert({ text: 'Failed to load transactions. Please try again later.' });
    });
  }


  // ===== Grid =====
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    setTimeout(() => this.gridApi.sizeColumnsToFit(), 0);

    const allColumnIds: (string | Column)[] = [];
    this.gridColumnApi.getColumns()?.forEach(col => allColumnIds.push(col.getId()));
    this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    this.onSearchPayments();
  }


  onPaginationChange(page: number) {
    if (!this.gridApi) return;

    this.currentPage = page;
    this.pageNumber = page - 1;

    this.gridApi.showLoadingOverlay();
    this.onSearchPayments();
  }

  onFilterModified(param: FilterModifiedEvent | any) {
    const temp = param?.filterInstance;
    this.filterObj[param?.column?.colId] = temp;
  }

  // ===== Column toggle =====
  updateSelectedColumn(event: any) {
    const index = Number(event.target.value);
    const column = this.columnDefsCopy[index];
    if (!column?.field || !this.gridColumnApi) return;

    const visible = !!event.target.checked;
    column.hide = !visible;
    this.gridColumnApi.setColumnVisible(column.field, visible);
  }

  // ===== Export =====
  onExportCSV() {
    const exportColumns = this.getExportableColumns();
    const headerFields = exportColumns.map(c => c.field!).filter(Boolean);

    const expData = this.rowData.map(row => {
      const obj: any = {};
      headerFields.forEach(f => obj[f] = row[f] ?? '');
      return obj;
    });

    const now = new Date();
    const referenceId =
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}` +
      `_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;

    const fileName = `CROSS_FX_Report_${referenceId}`;

    this.exportTrackingService.trackExcelExport(
      fileName,
      expData.length,
      'cross_fx_report',
      this.getCurrentFilters()
    ).subscribe({
      next: () => this.performExcelExport(expData, fileName, exportColumns),
      error: () => this.performExcelExport(expData, fileName, exportColumns)
    });
  }

  private performExcelExport(json: any[], excelFileName: string, exportColumns: ColDef[]): void {
    try {
      const workbook = XLSX.utils.book_new();
      const headerFields = exportColumns.map(c => c.field as string);

      const aoa: any[][] = [];
      aoa.push(headerFields.map(f => '')); // parent header row (empty)
      aoa.push(exportColumns.map(c => c.headerName || c.field)); // child header row

      json.forEach(record => {
        aoa.push(headerFields.map(field => record[field]));
      });

      const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cross Border FX Report');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', compression: true });
      this.saveAsExcelFile(excelBuffer, excelFileName);
    } catch (e) {
      console.error(e);
      this.alertService.errorAlertWithMessage('Failed to export data to Excel. Please try again.');
    }
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    FileSaver.saveAs(data, `${fileName}_${Date.now()}${EXCEL_EXTENSION}`);
    this.alertService.successAlert('Export Successful');
  }

  private getCurrentFilters(): any {
    return {
      dateRange: { from: this.fromDisplay, to: this.toDisplay },
      merchant: this.merchantList,
      status: this.status,
      currencyFrom: this.currencyFrom,
      currencyTo: this.currencyTo
    };
  }

  private getExportableColumns(): ColDef[] {
    return (this.tableSelectedColumn as ColDef[])
      .filter(c => !!c.field && c.field !== 'action')
      .filter(c => {
        const col = this.gridColumnApi?.getColumn(c.field!);
        return !col || col.isVisible();
      });
  }

  onCurrencyFromChange(value: string | null) {
    this.currencyFrom = value;

    this.currencyToList = this.CurrencyList.filter(c =>
      !value || value === 'ALL' ? true : c.FieldValue !== value
    );

    if (value && value !== 'ALL' && this.currencyTo === value) {
      this.currencyTo = null;
    }
  }

  onCurrencyToChange(value: string | null) {
    this.currencyTo = value;

    this.currencyFromList = this.CurrencyList.filter(c =>
      !value || value === 'ALL' ? true : c.FieldValue !== value
    );

    if (value && value !== 'ALL' && this.currencyFrom === value) {
      this.currencyFrom = null;
    }
  }

  isFullWidthRow = (params: any) => !!params?.rowNode?.data?.__detailRow;

  fullWidthCellRenderer = (params: any) => {
    const txnId = params.data?.parentTxnId || '';
    const loading = !!params.data?.loading;

    if (loading) {
      return `
      <div style="padding:12px; border:1px solid #ddd; border-radius:6px; background:#fff;">
        <b>Audit Trail:</b> ${txnId}<br/>
        Loading...
      </div>
    `;
    }

    const records = this.normalizeAuditEntries(params.data?.details);
    const formula = params.data?.formula ?? '';
    const rowCount = Math.max(records.length, 1);

    const headerHtml = `
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Transaction ID</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Display Name</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Currency Pair</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">ask</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">bid</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Mid</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Rate</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Timestamp</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Audit Step</th>
    <th style="padding:6px 10px; border:1px solid #ddd; background:#f3f3f3;">Calculation</th>
    <th style="padding:6px 10px; border:none; background:#f3f3f3;">Formula</th>
  `;

    const bodyHtml = (records.length ? records : [null]).map((rec: any, idx: number) => {
      // No audit rows
      if (!rec) {
        return `
        <tr>
          <td colspan="10" style="padding:10px; border:1px solid #ddd;">No audit entries</td>
          <td style="padding:10px; border:none; vertical-align:top;">${formula}</td>
        </tr>
      `;
      }

      // Normal audit row columns
      const commonTds = `
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.transactionId ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.displayName ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.fromCurrency ?? ''}/${rec.toCurrency ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.ask ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.bid ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.midPrice ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.exchangeRate ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.timestamp ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.auditStep ?? ''}</td>
      <td style="padding:6px 10px; border:1px solid #ddd;">${rec.crossCurrencyCalculation ?? ''}</td>
    `;

    const formulaTd = (idx === 0)
      ? `<td rowspan="${rowCount}"
            style="
              padding:6px 10px;
              border-left:1px solid #ddd;
              border-right:1px solid #ddd;
              border-top:1px solid #ddd;
              border-bottom:1px solid #ddd;
              vertical-align:middle;
              text-align:center;
              font-weight:600;
              white-space:pre-wrap;
              background:#fff;
            ">
            ${formula}
        </td>`
      : ``;



      return `<tr>${commonTds}${formulaTd}</tr>`;
    }).join('');

    return `
    <div style="padding:10px 12px; background:#fff; border:1px solid #dcdcdc; border-radius:6px;">
      <div style="font-weight:700; margin-bottom:8px;">Audit Trail: ${txnId}</div>
      <table style="width:100%; border-collapse:collapse; font-size:13px;">
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    </div>
  `;
  };


  /**
   * ✅ Make the detail row taller so table is visible
   */
  getRowHeight = (params: any) => {
    if (params.data?.__detailRow) {
      const rows = this.normalizeAuditEntries(params.data?.details);
      const rowCount = Math.max(rows.length, 1);
      return 95 + (rowCount * 36);
    }
    return 42;
  };


  onCellClicked(event: any) {
    if (event?.colDef?.field !== 'isCrossTxn') return;
    if (event?.data?.isCrossTxn !== true) return;

    const txnId: string = event?.data?.transactionId;
    if (!txnId) return;

    // collapse if already open
    if (this.expandedTxnId === txnId) {
      this.expandedTxnId = null;
      this.rowData = [...this.baseData];
      this.gridApi?.setRowData(this.rowData);
      return;
    }

    // open new one (close previous automatically)
    this.expandedTxnId = txnId;

    const masterRow = this.baseData.find(r => r.transactionId === txnId);
    if (!masterRow) return;

    // show loading row immediately
    masterRow.detailsLoading = true;
    this.rowData = this.buildRowsWithDetail(txnId);
    this.gridApi?.setRowData(this.rowData);

    // if already loaded before, don't call again
    if (masterRow.detailsLoaded) {
      masterRow.detailsLoading = false;
      this.rowData = this.buildRowsWithDetail(txnId);
      this.gridApi?.setRowData(this.rowData);
      return;
    }

    this.fxService.getFxTransactionAuditTrail(txnId).subscribe({
      next: (res: any) => {
        const auditEntries = res?.auditEntries ?? [];
        const formula = res?.summary?.calculationFormula ?? '';
        masterRow.details = auditEntries;
        masterRow.formula = formula;
        masterRow.detailsLoaded = true;
        masterRow.detailsLoading = false;

        this.rowData = this.buildRowsWithDetail(txnId);
        this.gridApi?.setRowData(this.rowData);
      },
      error: (err: any) => {
        console.error('Failed to load audit trail', err);
        masterRow.details = [];
        masterRow.detailsLoaded = true;
        masterRow.detailsLoading = false;

        this.rowData = this.buildRowsWithDetail(txnId);
        this.gridApi?.setRowData(this.rowData);

        this.alertService.errorAlert({ text: 'Failed to load audit trail details.' });
      }
    });
  }


  /**
   * ✅ Inserts a full-width "__detailRow" just below the expanded transaction row
   */
  private buildRowsWithDetail(txnId: string): any[] {
    const rows: any[] = [];

    for (const row of this.baseData) {
      rows.push(row);

      if (row.transactionId === txnId) {
        rows.push({
          __detailRow: true,
          parentTxnId: txnId,
          loading: !!row.detailsLoading,
          details: row.details,// <-- auditEntries[] or null
          formula: row.formula // ✅ pass formula to renderer
        });
      }
    }

    return rows;
  }


  private normalizeAuditEntries(details: any): any[] {
    if (!details) return [];
    if (!Array.isArray(details)) return [];
    return details;
  }


  /**
   * ✅ Converts details into a tabular structure for renderer:
   * - If details is array-of-array: [ [ {key,value},... ], [ {key,value},... ] ]
   *   => returns [ {Amount:.., Date:..}, {...} ]
   * - If details is array: [ {key,value}, {key,value} ]
   *   => returns [ {Amount:.., Date:..} ]
   */
  private normalizeDetails(details: any): any[] {
    if (!details) return [];

    // array of arrays
    if (Array.isArray(details) && Array.isArray(details[0])) {
      return (details as any[][]).map(group => {
        const obj: any = {};
        (group || []).forEach(kv => obj[kv.key] = kv.value);
        return obj;
      });
    }

    // single array
    if (Array.isArray(details)) {
      const obj: any = {};
      (details as any[]).forEach(kv => obj[kv.key] = kv.value);
      return [obj];
    }

    return [];
  }

}
